import type { ActiveState, Alarm } from './clockTypes.js';

/*
 * Per-classroom persistence for the clock subsystem. Active state and
 * the alarms list each get their own localStorage key so a tab refresh
 * mid-meeting doesn't lose a running countdown or scheduled cleanup
 * alarm. (When the backend classroom_settings column lands we'll
 * promote these the same way enabledPages and homeFeatures will.)
 */

const ACTIVE_KEY = (classroomId: string) => `classy:clockActive:${classroomId}`;
const ALARMS_KEY = (classroomId: string) => `classy:clockAlarms:${classroomId}`;

function safeLocal(): globalThis.Storage | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    return localStorage;
  } catch {
    return null;
  }
}

export function loadActive(classroomId: string): ActiveState {
  const store = safeLocal();
  if (!store) return { kind: 'idle' };
  try {
    const raw = store.getItem(ACTIVE_KEY(classroomId));
    if (!raw) return { kind: 'idle' };
    const parsed = JSON.parse(raw) as ActiveState;
    if (parsed.kind === 'countdown' || parsed.kind === 'timer' || parsed.kind === 'idle') {
      return parsed;
    }
    return { kind: 'idle' };
  } catch {
    return { kind: 'idle' };
  }
}

export function saveActive(classroomId: string, state: ActiveState): void {
  const store = safeLocal();
  if (!store) return;
  try {
    if (state.kind === 'idle') store.removeItem(ACTIVE_KEY(classroomId));
    else store.setItem(ACTIVE_KEY(classroomId), JSON.stringify(state));
  } catch {
    /* ignore quota / private-mode errors */
  }
}

export function loadAlarms(classroomId: string): Alarm[] {
  const store = safeLocal();
  if (!store) return [];
  try {
    const raw = store.getItem(ALARMS_KEY(classroomId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (a): a is Alarm =>
        a &&
        typeof a.id === 'string' &&
        typeof a.name === 'string' &&
        typeof a.hour === 'number' &&
        typeof a.minute === 'number' &&
        a.hour >= 0 &&
        a.hour <= 23 &&
        a.minute >= 0 &&
        a.minute <= 59,
    );
  } catch {
    return [];
  }
}

export function saveAlarms(classroomId: string, alarms: Alarm[]): void {
  const store = safeLocal();
  if (!store) return;
  try {
    if (alarms.length === 0) store.removeItem(ALARMS_KEY(classroomId));
    else store.setItem(ALARMS_KEY(classroomId), JSON.stringify(alarms));
  } catch {
    /* ignore */
  }
}
